import { Button } from '@daily/shared/components/Button';
import { CloseIcon } from '@daily/shared/components/Icons/CloseIcon';
import { Text } from '@daily/shared/components/Text';
import classNames from 'classnames';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CustomIntegration,
  useCustomIntegrations,
} from '../../hooks/useCustomIntegrations';
import { AppIcon } from './AppIcon';

interface Props {
  textVariant?: React.ComponentProps<typeof Text>['variant'];
  iconSize?: 16 | 24;
  iconVariant?: React.ComponentProps<typeof CloseIcon>['variant'];
  onClickApp?(integration: CustomIntegration): void;
}

export const AppsList: React.FC<React.PropsWithChildren<Props>> = memo(
  ({
    children,
    iconSize = 16,
    iconVariant = 'thick',
    onClickApp,
    textVariant = 'base',
  }) => {
    const { t } = useTranslation();
    const {
      canControlIntegration,
      mainIntegrations,
      runningMainIntegrations,
      runningSharedIntegrations,
      startIntegration,
      stopIntegration,
    } = useCustomIntegrations();
    const apps = mainIntegrations.filter(canControlIntegration);

    const handleClickApp = (integration: CustomIntegration) => {
      if (integration.running || integration.id in runningSharedIntegrations) {
        stopIntegration(integration.id);
      } else {
        startIntegration(integration.id);
      }
      onClickApp?.(integration);
    };

    return (
      <div className="apps-list">
        {children}
        {apps.map((app) => {
          const isRunning =
            runningMainIntegrations.some((a) => a.id === app.id) ||
            app.id in runningSharedIntegrations;
          return (
            <Button
              key={app.id}
              className={classNames({
                running: isRunning,
              })}
              onClick={() => handleClickApp(app)}
              title={t(
                isRunning ? 'integrations.close' : 'integrations.launch',
                {
                  label: app.label,
                }
              )}
              variant="secondary"
            >
              <AppIcon id={app.id} size={iconSize} />
              <Text variant={textVariant}>{app.label}</Text>
              {isRunning && (
                <CloseIcon
                  className="close"
                  size={iconSize}
                  variant={iconVariant}
                />
              )}
            </Button>
          );
        })}
        <style jsx>{`
          .apps-list {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }
          .apps-list :global(button) {
            display: flex;
            gap: 4px;
            min-width: 100px;
            padding: 8px 32px 8px 8px;
            position: relative;
            width: 100%;
          }
          .apps-list :global(button:not(.running)) {
            background: inherit;
          }
          .apps-list :global(button .close) {
            position: absolute;
            right: 8px;
            top: 50%;
            transform: translateY(-50%);
          }
        `}</style>
      </div>
    );
  }
);
AppsList.displayName = 'AppsList';
